import React, { useState, useEffect } from "react";
import { Settings, Plus, ArrowLeft, Merge } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import QuickQuizService, { LookupData } from "../../_services/QuickQuizService";
import QuizFormPanel from "./QuizFormPanel";
import QuizListPanel from "./QuizListPanel";
import QuizViewPanel from "./QuizViewPanel";
import TestMonitoringPanel from "./TestMonitoringPanel";
import WebProctoringPanel from "./WebProctoringPanel";
import TestResultsPanel from "./TestResultsPanel";
import MarksModerationPanel from "./MarksModerationPanel";
import TestQuestionsPanel from "./TestQuestionsPanel";
import MergeTestsPanel from "./MergeTestsPanel";
import ElementGuard from "../../access-control/ElementGuard";
import AccessControlEditorModal from "../../access-control/AccessControlEditorModal";
import "./quick-quiz-maker.css";

const PAGE_KEY = "quick-quiz-maker";

interface SectionState {
  name: string;
  questionIds: string;
  source: "question_bank" | "test_section";
  keywordId: string;
  subSections: Array<{
    name: string;
    questionIds: string;
    source: "question_bank" | "test_section";
  }>;
  inlineQuestions: Array<any>;
}

type Mode = "create" | "list" | "edit" | "view" | "monitoring" | "results" | "moderation" | "test-questions" | "merge-tests" | "web-proctoring";

const QuickQuizMakerPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = params.get("returnTo");
  const prefilledQuestionIds = params.get("questionIds");

  // Pre-fill from test-structure page (+ button in matrix cell)
  const prefillCategoryId = params.get("categoryId") || "";
  const prefillCourseId = params.get("courseId") ? Number(params.get("courseId")) : null;
  const prefillProgramId = params.get("programId") ? Number(params.get("programId")) : null;
  const prefillYearId = params.get("yearId") ? Number(params.get("yearId")) : null;
  const prefillTermId = params.get("termId") ? Number(params.get("termId")) : null;

  // Context names for the banner (passed from test-structure page)
  const prefillContext = (prefillProgramId && returnTo === "test-structure") ? {
    programName: params.get("programName") || "",
    yearName: params.get("yearName") || "",
    termName: params.get("termName") || "",
    courseName: params.get("courseName") || "",
    categoryName: params.get("categoryName") || "",
  } : null;

  // Restore mode and testId from URL on initial load
  const urlMode = params.get("mode") as Mode | null;
  const urlTestId = params.get("testId") ? Number(params.get("testId")) : null;

  // Default to "list" (Manage Tests). Honour explicit URL ?mode= and prefill
  // params (questionIds / categoryId) that imply a create flow.
  const initialMode: Mode =
    urlMode || (prefilledQuestionIds || prefillCategoryId ? "create" : "list");
  const [mode, setMode] = useState<Mode>(initialMode);
  const [lookups, setLookups] = useState<LookupData | null>(null);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [showAccessControlModal, setShowAccessControlModal] = useState(false);
  const [editingTestId, setEditingTestId] = useState<number | null>(urlMode === "edit" ? urlTestId : null);
  const [viewingTestId, setViewingTestId] = useState<number | null>(urlMode === "view" ? urlTestId : null);
  const [monitoringTestId, setMonitoringTestId] = useState<number | null>(urlMode === "monitoring" ? urlTestId : null);
  const [resultsTestId, setResultsTestId] = useState<number | null>(urlMode === "results" ? urlTestId : null);
  const [moderationTestId, setModerationTestId] = useState<number | null>(urlMode === "moderation" ? urlTestId : null);
  const [testQuestionsTestId, setTestQuestionsTestId] = useState<number | null>(urlMode === "test-questions" ? urlTestId : null);
  // Web proctoring screen targets a single student (testId + enquiryId), opened
  // in a new tab from the monitoring screen's camera icon.
  const urlEnquiryId = params.get("enquiryId") ? Number(params.get("enquiryId")) : null;
  const [proctoringTestId, setProctoringTestId] = useState<number | null>(urlMode === "web-proctoring" ? urlTestId : null);
  const [proctoringEnquiryId, setProctoringEnquiryId] = useState<number | null>(urlMode === "web-proctoring" ? urlEnquiryId : null);
  const [editLoading, setEditLoading] = useState(false);
  const [initialFormData, setInitialFormData] = useState<{
    testName: string;
    testDate: string;
    testTime: string;
    categoryId: string;
    testTypeId: string;
    playerId: string;
    testResultTypeId: string;
    startDate?: string;
    endDate?: string;
    testCutOffTime?: string;
    yearIds?: number[];
    instructions?: string;
    mobileInstructions?: string;
    testDeclarationRemarks?: string;
    studentAnnouncementRemarks?: string;
    sections: SectionState[];
  } | undefined>(undefined);
  const [formKey, setFormKey] = useState(0); // force re-mount on mode change

  // One-shot signal: when set, QuizViewPanel auto-pops the Edit Test Info
  // modal as soon as the quiz finishes loading. Cleared by the panel.
  const [autoOpenEditFor, setAutoOpenEditFor] = useState<number | null>(null);

  const [success, setSuccess] = useState<{
    testId: number;
    totalQuestions: number;
    totalMarks: number;
  } | null>(null);

  const handleQuickEdit = (id: number) => {
    setViewingTestId(id);
    setAutoOpenEditFor(id);
    setMode("view");
  };

  // Sync mode & testId to URL query params
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const currentTestId =
      mode === "view" ? viewingTestId :
      mode === "edit" ? editingTestId :
      mode === "monitoring" ? monitoringTestId :
      mode === "results" ? resultsTestId :
      mode === "moderation" ? moderationTestId :
      mode === "web-proctoring" ? proctoringTestId :
      mode === "test-questions" ? testQuestionsTestId : null;

    if (mode === "create" || mode === "list") {
      p.delete("mode");
      p.delete("testId");
      p.delete("enquiryId");
    } else {
      p.set("mode", mode);
      if (currentTestId) p.set("testId", String(currentTestId));
      else p.delete("testId");
      // enquiryId is only meaningful for the web-proctoring screen
      if (mode === "web-proctoring" && proctoringEnquiryId) p.set("enquiryId", String(proctoringEnquiryId));
      else p.delete("enquiryId");
    }

    const newSearch = p.toString();
    if (newSearch !== location.search.replace(/^\?/, "")) {
      history.replace({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" });
    }
  }, [mode, viewingTestId, editingTestId, monitoringTestId, resultsTestId, moderationTestId, testQuestionsTestId, proctoringTestId, proctoringEnquiryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: if URL has edit mode, load quiz for editing
  useEffect(() => {
    if (urlMode === "edit" && urlTestId) {
      loadQuizForEdit(urlTestId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load lookups on mount
  useEffect(() => {
    QuickQuizService.getLookups()
      .then(setLookups)
      .catch((err) => console.error("Failed to load lookups:", err))
      .finally(() => setLookupsLoading(false));
  }, []);

  // Pre-populate from URL questionIds (e.g. from DOCX import)
  useEffect(() => {
    if (prefilledQuestionIds) {
      setInitialFormData({
        testName: "",
        testDate: "",
        testTime: "",
        categoryId: "",
        testTypeId: "",
        playerId: "",
        testResultTypeId: "",
        sections: [
          {
            name: "Section 1",
            questionIds: prefilledQuestionIds,
            source: "question_bank",
            keywordId: "",
            subSections: [],
            inlineQuestions: [],
          },
        ],
      });
      setFormKey((k) => k + 1);
    }
  }, [prefilledQuestionIds]);

  // Pre-populate from test-structure page (categoryId, yearId, courseName as testName)
  useEffect(() => {
    if (prefillCategoryId && !prefilledQuestionIds && urlMode === "create") {
      const courseName = params.get("courseName") || "";
      setInitialFormData({
        testName: courseName,
        testDate: "",
        testTime: "",
        categoryId: prefillCategoryId,
        testTypeId: "",
        playerId: "",
        testResultTypeId: "",
        yearIds: prefillYearId ? [prefillYearId] : [],
        sections: [{ name: "", questionIds: "", source: "question_bank", keywordId: "", subSections: [], inlineQuestions: [] }],
      });
      setFormKey((k) => k + 1);
    }
  }, [prefillCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load quiz for editing
  const loadQuizForEdit = async (testId: number) => {
    setEditLoading(true);
    try {
      const quiz = await QuickQuizService.getQuiz(testId);

      const loadedSections: SectionState[] = quiz.sections.map((s) => ({
        name: s.name,
        questionIds: s.questions
          .map((q) => String(q.questionId || q.mappingId))
          .join(", "),
        source: (s.questions.length > 0 && s.questions[0]?.questionId
          ? "question_bank"
          : "test_section") as "question_bank" | "test_section",
        keywordId: s.keywordId ? String(s.keywordId) : "",
        inlineQuestions: [],
        subSections: s.subSections.map((sub) => ({
          name: sub.name,
          questionIds: sub.questions
            .map((q: any) => String(q.questionId || q.mappingId))
            .join(", "),
          source: (sub.questions.length > 0 && sub.questions[0]?.questionId
            ? "question_bank"
            : "test_section") as "question_bank" | "test_section",
        })),
      }));

      // Helpers: convert ISO/MySQL datetime to HTML-input-friendly formats
      const toDateOnly = (d?: string | null): string => {
        if (!d) return "";
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return "";
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
      };
      const toDateTimeLocal = (d?: string | null): string => {
        if (!d) return "";
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return "";
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      };

      setInitialFormData({
        testName: quiz.name,
        testDate: toDateOnly(quiz.testDate),
        testTime: quiz.testTime ? String(quiz.testTime) : "",
        categoryId: quiz.categoryId ? String(quiz.categoryId) : "",
        testTypeId: quiz.testTypeId ? String(quiz.testTypeId) : "",
        playerId: quiz.playerId ? String(quiz.playerId) : "",
        testResultTypeId: quiz.testResultTypeId ? String(quiz.testResultTypeId) : "",
        startDate: toDateTimeLocal(quiz.startDate),
        endDate: toDateTimeLocal(quiz.endDate),
        testCutOffTime: toDateTimeLocal(quiz.testCutOffTime),
        yearIds: quiz.yearIds || [],
        instructions: quiz.instructions || "",
        mobileInstructions: quiz.mobileInstructions || "",
        testDeclarationRemarks: quiz.testDeclarationRemarks || "",
        studentAnnouncementRemarks: quiz.studentAnnouncementRemarks || "",
        sections:
          loadedSections.length > 0
            ? loadedSections
            : [
                {
                  name: "",
                  questionIds: "",
                  source: "question_bank",
                  keywordId: "",
                  subSections: [],
                  inlineQuestions: [],
                },
              ],
      });
      setEditingTestId(testId);
      setMode("edit");
      setFormKey((k) => k + 1);
    } catch (err: any) {
      console.error("Failed to load quiz:", err);
      alert(err.message || "Failed to load quiz for editing");
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTestId(null);
    setInitialFormData(undefined);
    setMode("create");
    setFormKey((k) => k + 1);
  };

  const handleSuccess = async (result: {
    testId: number;
    totalQuestions: number;
    totalMarks: number;
  }) => {
    // Auto-issue to course+program+term if coming from test-structure page
    if (prefillCourseId && prefillProgramId) {
      try {
        await QuickQuizService.issueToCourse(result.testId, {
          courseId: prefillCourseId,
          programId: prefillProgramId,
          ...(prefillTermId ? { termId: prefillTermId } : {}),
        });
      } catch (e) {
        console.error("Auto-issue to course/program failed:", e);
      }
    }
    // Auto-add year mapping if yearId was pre-filled
    if (prefillYearId) {
      try {
        await QuickQuizService.updateTestInfo(result.testId, { yearIds: [prefillYearId] });
      } catch (e) {
        console.error("Auto-set year failed:", e);
      }
    }
    setSuccess(result);
  };

  const handleCreateAnother = () => {
    setSuccess(null);
    setEditingTestId(null);
    setInitialFormData(undefined);
    setMode("create");
    setFormKey((k) => k + 1);
  };

  const goBackToTestStructure = () => {
    const qs = new URLSearchParams();
    if (prefillProgramId) qs.set("programId", String(prefillProgramId));
    if (prefillYearId) qs.set("yearId", String(prefillYearId));
    const ctId = new URLSearchParams(location.search).get("courseTypeId");
    if (ctId) qs.set("courseTypeId", ctId);
    history.push(`/test-structure${qs.toString() ? `?${qs}` : ""}`);
  };

  // Render success state
  if (success) {
    return (
      <div className="qqm-page">
        <div className="qqm-success">
          <div className="qqm-success-icon">&#10003;</div>
          <h4>
            {editingTestId ? "Quiz Updated Successfully!" : "Quiz Created Successfully!"}
          </h4>
          <p className="qqm-success-meta">
            Test ID: <strong>{success.testId}</strong> &nbsp;·&nbsp; Questions:{" "}
            <strong>{success.totalQuestions}</strong> &nbsp;·&nbsp; Total Marks:{" "}
            <strong>{success.totalMarks}</strong>
          </p>
          <div className="qqm-success-actions">
            <button type="button" className="qqm-btn-primary" onClick={handleCreateAnother}>
              <Plus size={16} />
              Create Another
            </button>
            <button
              type="button"
              className="qqm-btn-ghost"
              onClick={() => {
                setSuccess(null);
                setMode("list");
              }}
            >
              Manage Tests
            </button>
            {returnTo === "test-structure" && (
              <button type="button" className="qqm-btn-ghost" onClick={goBackToTestStructure}>
                Back to Test Structure
              </button>
            )}
            {returnTo === "study-resources" && (
              <button
                type="button"
                className="qqm-btn-ghost"
                onClick={() => history.push("/study-resources")}
              >
                Back to Study Resources
              </button>
            )}
            {returnTo === "question-bank-import" && (
              <button
                type="button"
                className="qqm-btn-ghost"
                onClick={() => history.push("/question-bank/import")}
              >
                Back to Import
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (lookupsLoading) {
    return (
      <div className="qqm-page">
        <div className="qqm-center-state">
          <div className="qqm-spinner" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  const isCreatingNew = mode === "create" && !editingTestId;
  const showStatePill = mode !== "list" && mode !== "create";
  const showBackToList = mode !== "list";

  return (
    <div className="qqm-page">
      {/* Header */}
      <div className="qqm-header">
        <div className="qqm-header-left">
          <h1 className="qqm-title">Quick Quiz Maker</h1>
          <span className="qqm-subtitle">Manage your tests and quizzes</span>
          <ElementGuard pageKey={PAGE_KEY} adminOnly>
            <button
              type="button"
              onClick={() => setShowAccessControlModal(true)}
              title="Page access control — roles & permissions"
              className="qqm-icon-btn"
              aria-label="Page access control"
            >
              <Settings size={16} />
            </button>
          </ElementGuard>
        </div>
        <div className="qqm-header-right">
          {returnTo === "test-structure" && (
            <button type="button" className="qqm-btn-ghost" onClick={goBackToTestStructure}>
              <ArrowLeft size={14} />
              Test Structure
            </button>
          )}
          {returnTo === "study-resources" && (
            <button
              type="button"
              className="qqm-btn-ghost"
              onClick={() => history.push("/study-resources")}
            >
              <ArrowLeft size={14} />
              Study Resources
            </button>
          )}
          {returnTo === "question-bank-import" && (
            <button
              type="button"
              className="qqm-btn-ghost"
              onClick={() => history.push("/question-bank/import")}
            >
              <ArrowLeft size={14} />
              Import
            </button>
          )}
          {mode !== "merge-tests" && (
            <button
              type="button"
              className="qqm-fab qqm-fab-merge"
              onClick={() => setMode("merge-tests")}
              title="Merge sequence tests"
            >
              <span className="qqm-fab-plus">
                <Merge size={14} strokeWidth={2.5} />
              </span>
              Merge Tests
            </button>
          )}
          {!isCreatingNew && (
            <button
              type="button"
              className="qqm-fab"
              onClick={() => {
                if (mode !== "create") handleCancelEdit();
              }}
              title="Create a new quiz"
            >
              <span className="qqm-fab-plus">
                <Plus size={14} strokeWidth={2.5} />
              </span>
              New Quiz
            </button>
          )}
        </div>
      </div>

      {/* Test Structure context banner */}
      {prefillContext && mode === "create" && (
        <div className="qqm-context">
          <span className="qqm-context-label">Creating for</span>
          {prefillContext.programName && (
            <span className="qqm-context-item">
              <span className="qqm-context-key">Program</span>
              <span className="qqm-context-val">{prefillContext.programName}</span>
            </span>
          )}
          {prefillContext.yearName && (
            <span className="qqm-context-item">
              <span className="qqm-context-key">Year</span>
              <span className="qqm-context-val">{prefillContext.yearName}</span>
            </span>
          )}
          {prefillContext.termName && (
            <span className="qqm-context-item">
              <span className="qqm-context-key">Term</span>
              <span className="qqm-context-val">{prefillContext.termName}</span>
            </span>
          )}
          {prefillContext.courseName && (
            <span className="qqm-context-item">
              <span className="qqm-context-key">Course</span>
              <span className="qqm-context-val">{prefillContext.courseName}</span>
            </span>
          )}
          {prefillContext.categoryName && (
            <span className="qqm-context-item">
              <span className="qqm-context-key">Category</span>
              <span className="qqm-context-val is-emphasis">
                {prefillContext.categoryName}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Mode bar — back link + state pill */}
      {(showBackToList || showStatePill || isCreatingNew) && (
        <div className="qqm-modebar">
          {showBackToList && (
            <button
              type="button"
              className="qqm-btn-ghost"
              onClick={() => {
                handleCancelEdit();
                setMode("list");
              }}
            >
              <ArrowLeft size={14} />
              Manage Tests
            </button>
          )}
          {isCreatingNew && (
            <span className="qqm-state-pill qqm-state-create">
              <span className="qqm-state-dot" />
              Creating new quiz
            </span>
          )}
          {mode === "edit" && editingTestId && (
            <span className="qqm-state-pill qqm-state-edit">
              <span className="qqm-state-dot" />
              Editing Test #{editingTestId}
            </span>
          )}
          {mode === "view" && viewingTestId && (
            <span className="qqm-state-pill qqm-state-view">
              <span className="qqm-state-dot" />
              Viewing Test #{viewingTestId}
            </span>
          )}
          {mode === "results" && resultsTestId && (
            <span className="qqm-state-pill qqm-state-results">
              <span className="qqm-state-dot" />
              Results Test #{resultsTestId}
            </span>
          )}
          {mode === "monitoring" && monitoringTestId && (
            <span className="qqm-state-pill qqm-state-monitor">
              <span className="qqm-state-dot" />
              Monitoring Test #{monitoringTestId}
            </span>
          )}
          {mode === "moderation" && moderationTestId && (
            <span className="qqm-state-pill qqm-state-moderate">
              <span className="qqm-state-dot" />
              Marks Moderation Test #{moderationTestId}
            </span>
          )}
          {mode === "merge-tests" && (
            <span className="qqm-state-pill qqm-state-view">
              <span className="qqm-state-dot" />
              Merging tests
            </span>
          )}
        </div>
      )}

      {/* Edit loading overlay */}
      {editLoading && (
        <div className="qqm-center-state">
          <div className="qqm-spinner" aria-hidden="true" />
          <span>Loading test for editing…</span>
        </div>
      )}

      {/* Content */}
      {!editLoading && mode === "view" && viewingTestId && (
        <QuizViewPanel
          testId={viewingTestId}
          autoOpenEdit={autoOpenEditFor === viewingTestId}
          onAutoOpenEditConsumed={() => setAutoOpenEditFor(null)}
          onEdit={(id) => {
            setViewingTestId(null);
            loadQuizForEdit(id);
          }}
          onBack={() => {
            setViewingTestId(null);
            setMode("list");
          }}
          onMonitor={(id) => {
            setMonitoringTestId(id);
            setMode("monitoring");
          }}
          onResult={(id) => {
            setResultsTestId(id);
            setMode("results");
          }}
          onModerate={(id) => {
            setModerationTestId(id);
            setMode("moderation");
          }}
          onTestQuestions={(id) => {
            setTestQuestionsTestId(id);
            setMode("test-questions");
          }}
        />
      )}

      {!editLoading && mode === "results" && resultsTestId && (
        <TestResultsPanel
          testId={resultsTestId}
          onBack={() => {
            setResultsTestId(null);
            if (viewingTestId) {
              setMode("view");
            } else {
              setMode("list");
            }
          }}
          onViewTest={(id) => {
            setResultsTestId(null);
            setViewingTestId(id);
            setMode("view");
          }}
        />
      )}

      {!editLoading && mode === "monitoring" && monitoringTestId && (
        <TestMonitoringPanel
          testId={monitoringTestId}
          onBack={() => {
            setMonitoringTestId(null);
            if (viewingTestId) {
              setMode("view");
            } else {
              setMode("list");
            }
          }}
          onViewTest={(id) => {
            setMonitoringTestId(null);
            setViewingTestId(id);
            setMode("view");
          }}
        />
      )}

      {!editLoading && mode === "web-proctoring" && proctoringTestId && proctoringEnquiryId && (
        <WebProctoringPanel
          testId={proctoringTestId}
          enquiryId={proctoringEnquiryId}
          onBack={() => {
            const tId = proctoringTestId;
            setProctoringEnquiryId(null);
            setProctoringTestId(null);
            setMonitoringTestId(tId);
            setMode("monitoring");
          }}
        />
      )}

      {!editLoading && mode === "moderation" && moderationTestId && (
        <MarksModerationPanel
          testId={moderationTestId}
          onBack={() => {
            setModerationTestId(null);
            if (viewingTestId) {
              setMode("view");
            } else {
              setMode("list");
            }
          }}
          onViewTest={(id) => {
            setModerationTestId(null);
            setViewingTestId(id);
            setMode("view");
          }}
        />
      )}

      {!editLoading && mode === "test-questions" && testQuestionsTestId && (
        <TestQuestionsPanel
          testId={testQuestionsTestId}
          onBack={() => {
            setTestQuestionsTestId(null);
            if (viewingTestId) {
              setMode("view");
            } else {
              setMode("list");
            }
          }}
          onViewTest={(id) => {
            setTestQuestionsTestId(null);
            setViewingTestId(id);
            setMode("view");
          }}
        />
      )}

      {/* Keep the list MOUNTED across view/edit navigation (hidden, not
          unmounted) so the search filters, loaded results, and pagination
          survive a round-trip to the View screen. Unmounting here reset every
          filter to its default and reloaded all tests on return. */}
      <div style={{ display: !editLoading && mode === "list" ? undefined : "none" }}>
        <QuizListPanel
          onEdit={loadQuizForEdit}
          onView={(id) => {
            setViewingTestId(id);
            setMode("view");
          }}
          onQuickEdit={handleQuickEdit}
        />
      </div>

      {!editLoading && mode === "merge-tests" && (
        <MergeTestsPanel
          onBack={() => setMode("list")}
          onView={(id) => {
            setViewingTestId(id);
            setMode("view");
          }}
        />
      )}

      {!editLoading && (mode === "create" || mode === "edit") && (
        <QuizFormPanel
          key={formKey}
          lookups={lookups}
          editingTestId={editingTestId}
          initialData={initialFormData}
          onSuccess={handleSuccess}
          onCancelEdit={editingTestId ? handleCancelEdit : undefined}
        />
      )}

      <ElementGuard pageKey={PAGE_KEY} adminOnly>
        <AccessControlEditorModal
          pageKey={PAGE_KEY}
          show={showAccessControlModal}
          onClose={() => setShowAccessControlModal(false)}
        />
      </ElementGuard>
    </div>
  );
};

export default QuickQuizMakerPage;
